/**
 * Shared social-account lookup, cached via TanStack Query.
 *
 * FEAT-095 / S-401 retirement — SocialCalendarView.tsx and AmplificationsPanel.tsx
 * both need to resolve a social_calendar row's account_id to a display name,
 * scoped to the row's own workspace (an owned account, or one granted to it —
 * same GET /agencyclients/:id/social-accounts list SocialAccountsView.tsx and
 * AmplifiersView.tsx's rule picker already use). There is no GET
 * /social-accounts/:id — a granted account only resolves correctly through
 * this per-workspace list, so this fetches that list once (react-query dedupes
 * by [agencyclientId], so many rows in the same workspace share one request)
 * and indexes into it client-side, rather than adding a new endpoint that
 * would have to reimplement resolveGrantedOrOwnedAccount's grant logic again.
 */
import { useApiQuery } from "@/lib/api";

export interface SocialAccountLookupRow {
  id: number;
  platform: string;
  display_name: string;
  is_shared_to_me: boolean;
}

export function useSocialAccountsForWorkspace(agencyclientId: number | null | undefined) {
  const { data } = useApiQuery<{ data: SocialAccountLookupRow[] }>(
    ["agencyclient-social-accounts", agencyclientId ?? 0],
    `/agencyclients/${agencyclientId ?? 0}/social-accounts`,
    {
      enabled: agencyclientId != null,
      staleTime: 60_000,
      retry: false,
    }
  );
  return data?.data ?? [];
}

export function useSocialAccountLookup(
  agencyclientId: number | null | undefined,
  accountId: number | null | undefined
): SocialAccountLookupRow | undefined {
  const accounts = useSocialAccountsForWorkspace(agencyclientId);
  return accountId != null ? accounts.find((a) => a.id === accountId) : undefined;
}

export function socialAccountDisplayName(
  account: SocialAccountLookupRow | undefined,
  accountId: number
): string {
  if (!account) return `Account #${accountId}`;
  return account.is_shared_to_me ? `${account.display_name} (shared)` : account.display_name;
}
